/* eslint-disable @typescript-eslint/naming-convention */
import adsCreative from './../store/modules/ads_creative/index';
import { isEmpty, pick } from 'lodash';
import request from './../utils/request/index';
import apis from './../const/apis';
import { captureError, customReport } from './../utils/errorLogger';
import { getCoverInfo } from './../service/index';

import { DraftType } from './../const/common';
import { AdSources, CreativeInfo, IdentityType, MaterialSource, MaterialType, videoStage } from './../const/media';
import { getAccountTodayHMS } from './../utils/dateUtil';
import { createFakeIdentity, uploadCreative } from './../service/media';
import { sleep } from './../utils/creativeUtil';
import { trackEvent } from './../tea-tracker';
import { showToast } from './../utils/msgUtil';
import i18n from './../i18n';
import identityStore from './../store/modules/identity/index';
import { ExternalType, SmartBidType } from './../const/state/adgroup';
import { OptimizeGoal, PricingType } from './../const/state/target';
import { FLOW_TYPE } from './../const/state/inventory-type';
import { automaticAudience, formatAudienceV2 } from './../utils/fastCreationFormat';
import { AdvancedCreativeType, imageMode } from './../const/creative';
import { campaignObjectiveI18nKey } from './../const/state/campaign';
import { AutomatedTargetingType } from './../store/modules/ads_creative/const';
import { useObjectiveHooks } from './../hooks/creation/objective/objectiveStatus';
import { useIdentityHooks } from './../hooks/global/useIdentityHooks';
import store from './../store/index';
import interestBehaviorState from './../store/modules/smart-interests-and-behaviors/index';
import { IS_VALID_OBJECTIVE_TYPE } from './audience/utils';

const { isLead, isEngagement, isTraffic, isReach, isVideoView } = useObjectiveHooks();
const { useTikTokIdentity, curIdentity, displayName, avatarIcon } = useIdentityHooks();
const updateObjective = (objective): void => {
  const { objective_type } = objective;
  adsCreative.mutations.setObjective(objective_type);
  // Set Smart-Interest-Behavior state to false if switched to Product Sales or Community Interaction objective
  // else reset to default state for smart-interest-behavior
  if (!IS_VALID_OBJECTIVE_TYPE.value) {
    interestBehaviorState.mutations.removeSmartInterestState();
  } else {
    interestBehaviorState.mutations.resetDefaultValues();
  }
};
const updateMedia = (media): void => {
  const { activeSource, draftIdentity, creativeInfo } = media;
  const aadvid = store.state?.account?.accountInfo?.id || '';
  identityStore.mutations.setCurIdentity(draftIdentity);
  adsCreative.mutations.setActiveSource(activeSource);
  adsCreative.mutations.setUsingTikTokAccountUnderOwner(false);
  if (media.useTikTokIdentity) {
    const id = localStorage.getItem(`selectedId-${aadvid}`);
    const localStorageIdentity = JSON.parse(id);
    identityStore.mutations.setUseTikTokIdentity(true);
    identityStore.mutations.setCurIdentity(localStorageIdentity);
    identityStore.mutations.setDisplayName(localStorageIdentity?.display_name);
    identityStore.mutations.setAvatarIcon(localStorageIdentity?.avatar_icon);
  }
  if (activeSource === MaterialSource.TIKTOK_VIDEO) {
    const TTVideo = localStorage.getItem(`TikTokVideo-${aadvid}`);
    const ifTikTokVideoExist = TTVideo.length > 0;
    ifTikTokVideoExist
      ? adsCreative.mutations.setSelectedVideo(JSON.parse(TTVideo))
      : adsCreative.mutations.setClearSelectedVideo();
    adsCreative.mutations.setClearCreativeData();
    adsCreative.mutations.setVideoStage(ifTikTokVideoExist ? videoStage.FINISH : videoStage.INIT);
    identityStore.mutations.setUseTikTokIdentity(true);
    return;
  }
  adsCreative.mutations.setCreativeData(creativeInfo);
  if (!isEmpty(creativeInfo)) {
    adsCreative.mutations.setVideoStage(videoStage.FINISH);
  }
};
const updateCreative = (creative): void => {
  const { create, externalUrl, instantForm } = creative;
  const { avatar, title, cta, sourceName } = create;
  if (sourceName) {
    identityStore.mutations.setDisplayName(sourceName);
    identityStore.mutations.setLocalDisplayName(sourceName);
  }
  adsCreative.mutations.setTitle(title);
  adsCreative.mutations.setCallToAction(cta);
  adsCreative.mutations.setExternalUrl(externalUrl);
  adsCreative.mutations.setInstantForm(instantForm);
  identityStore.mutations.setAvatarIcon(avatar);
};
const updateAudience = (audience): void => {
  const { age, interest, language, location, gender } = audience;
  adsCreative.mutations.setGender(gender);
  adsCreative.mutations.setAgeOption(age);
  adsCreative.mutations.setInterest(interest);
  adsCreative.mutations.setLanguages(language);
  adsCreative.mutations.setLocationsCity(location?.city || []);
  adsCreative.mutations.setLocationsCountry(location?.country || []);
  adsCreative.mutations.setLocationsProvince(location?.province || []);
  adsCreative.mutations.setLocationsDistricts(location?.districts || []);
  adsCreative.mutations.setLocationsVal(location?.value || []);
};
const updateBudget = (budget): void => {
  const { scheduleType, curBudget, startTime, endTime } = budget;
  adsCreative.mutations.setCurBudget(curBudget);
  adsCreative.mutations.setScheduleType(scheduleType);
  adsCreative.mutations.setStartTime(startTime);
  adsCreative.mutations.setEndTime(endTime);
};
export const setDraft = (draft): void => {
  adsCreative.mutations.setSetFromDraft(true);
  const aadvid = store.state?.account?.accountInfo?.id || '';
  const { objective = {}, audience = {}, creative = {}, budget = {}, media = {} } = draft;
  updateObjective(objective);
  updateAudience(audience);
  if (media?.activeSource === AdSources.NATIVE_VIDEO) {
    updateMedia(JSON.parse(localStorage.getItem(`mediaInfo-${aadvid}`)));
    creative.create = JSON.parse(localStorage.getItem(`createInfo-${aadvid}`));
  } else {
    updateMedia(media);
  }
  updateCreative(creative);
  updateBudget(budget);
};
export const submitFakeIdentity = async (globalLoading: any = null): Promise<any> => {
  const usingTikTokAuthOrOwn = useTikTokIdentity.value;
  if (usingTikTokAuthOrOwn) {
    return Promise.resolve();
  }
  try {
    const { identity_info } = await createFakeIdentity({
      identity_type: IdentityType.FAKE_USER,
      display_name: identityStore?.state?.identity?.displayName?.value?.toString?.() || '',
      profile_image: identityStore?.state?.identity?.avatarIcon.value?.web_uri || '',
    });

    identityStore.mutations.setCurIdentity(identity_info);
    // identity生成到可以被读取有延迟，需要主动等待
    await sleep(750);
    return Promise.resolve();
  } catch (e) {
    captureError(e);
    trackEvent('adp_waplite_impression', {
      fastcr_results_failed: 'Identity Save接口报错',
    });
    if (globalLoading) {
      globalLoading.hide();
    }
    showToast({
      message: e?.msg || (i18n.t('common_retry') as string),
    });
    return Promise.reject();
  }
};
export const saveDraft = async (): Promise<any> => {
  try {
    const aadvid = store.state?.account?.accountInfo?.id || '';
    const source = adsCreative.getters.identityFormData.value.activeSource;
    const { selectedVideo } = adsCreative.getters.identityFormData.value;
    const { creativeInfo } = adsCreative.getters.identityFormData.value;
    const media = {
      useTikTokIdentity: useTikTokIdentity.value,
      activeSource: source,
      libraryChosenType: MaterialType.VIDEO,
      curIdentity: curIdentity.value,
      creativeInfo:
        source === MaterialSource.TIKTOK_VIDEO
          ? selectedVideo
          : adsCreative.getters.identityFormData.value.creativeInfo,
    };
    const create = {
      avatar: identityStore.getters.identityFormData.value.avatarIcon,
      cta: adsCreative.getters.creativeFormData.value.callToAction,
      sourceName: displayName.value,
      title: adsCreative.getters.creativeFormData.value.title,
    };
    if (source === MaterialSource.TIKTOK_VIDEO) {
      localStorage.setItem(`TikTokVideo-${aadvid}`, JSON.stringify(selectedVideo));
    }
    if (useTikTokIdentity.value) {
      localStorage.setItem(`selectedId-${aadvid}`, JSON.stringify(curIdentity.value));
      localStorage.setItem(`mediaInfo-${aadvid}`, JSON.stringify(media));
      localStorage.setItem(`createInfo-${aadvid}`, JSON.stringify(create));
    }
    const draftJson = {
      objective: {
        objective_type: adsCreative.getters.campaignFormData.value.objectiveType,
      },
      budget: {
        scheduleType: adsCreative.getters.budgetFormData.value.scheduleType,
        budgetMode: adsCreative.getters.budgetFormData.value.budgetMode,
        curBudget: adsCreative.getters.budgetFormData.value.budget,
        startTime: adsCreative.getters.budgetFormData.value.startTime,
        endTime: adsCreative.getters.budgetFormData.value.endTime,
      },
      audience: {
        age: adsCreative.getters.audienceFormData.value.age.value,
        gender: adsCreative.getters.audienceFormData.value.gender.value,
        interest: adsCreative.getters.audienceFormData.value.interest.value,
        language: adsCreative.getters.audienceFormData.value.language.value,
        location: {
          city: adsCreative.getters.audienceFormData.value.location.city,
          country: adsCreative.getters.audienceFormData.value.location.country,
          province: adsCreative.getters.audienceFormData.value.location.province,
          value: adsCreative.getters.audienceFormData.value.location.value,
        },
        ...(interestBehaviorState.getters.getDisplayToggle.value
          ? {
              smart_interest_behavior: interestBehaviorState.getters.getSmartInterestSelection.value,
            }
          : {}),
      },
      creative: {
        create,
        is_creative_authorized: adsCreative.getters.creativeFormData.value.isCreativeAuthorized,
        ...(!isTraffic.value
          ? {}
          : {
              externalUrl: adsCreative.getters.creativeFormData.value.externalUrl,
            }),
        instantForm: adsCreative.getters.creativeFormData.value.instantForm,
      },
      media,
    };
    await request.post(
      apis.adgroup.set_draft,
      {
        draft_json: draftJson,
      },
      {
        params: {
          draft_type: DraftType.fastcreation,
        },
        shouldRetry(data: { code: number }) {
          return data.code !== 0;
        },
      },
    );
    return;
  } catch (e) {
    customReport.counter('customError', e);
  }
};
const getAdGroupExtraParams = (): Record<string, any> => {
  const inventoryFlow = [3000];
  if (isLead.value) {
    // 目前leadads 默认选择 max-cv no-bid
    return {
      smart_bid_type: SmartBidType.MAX_CV_NO_BID,
      inventory_flow: inventoryFlow,
      optimize_goal: OptimizeGoal.lead,
      inventory_flow_type: FLOW_TYPE.SELECT,
      pricing: PricingType.OCPM,
      cpa_phrase: 2,
      cpa_skip_first_phrase: 1, // 相当于boolean，默认跳过第一阶段
      external_type: ExternalType.leadAds,
    };
  }
  if (isEngagement.value) {
    return {
      smart_bid_type: SmartBidType.MAX_CV_NO_BID,
      inventory_flow: inventoryFlow,
      inventory_flow_type: FLOW_TYPE.SELECT,
      optimize_goal: OptimizeGoal.follow,
      pricing: PricingType.OCPM,
      cpa_phrase: 2,
      cpa_skip_first_phrase: 1,
      external_type: ExternalType.engagement,
    };
  }
  if (isReach.value) {
    return {
      external_type: ExternalType.engagement,
      inventory_flow: inventoryFlow,
      inventory_flow_type: FLOW_TYPE.SELECT,
      optimize_goal: OptimizeGoal.reach,
      frequency: 3,
      frequency_schedule: 7,
      pricing: 1,
    };
  }
  if (isVideoView.value) {
    return {
      external_type: ExternalType.engagement,
      inventory_flow: inventoryFlow,
      inventory_flow_type: FLOW_TYPE.SELECT,
      // optimize_goal: OptimizeGoal.videoView,
      optimize_goal: 118,
      pricing: 10,
    };
  }
  if (curIdentity.value.identity_type === IdentityType.TT_USER) {
    return {
      inventory_flow: inventoryFlow,
      inventory_flow_type: FLOW_TYPE.SELECT,
    };
  }
  // website 不加额外参数
  return {};
};
const getCoverInfoAPI = async (vid): Promise<any> => {
  try {
    const webUri = await getCoverInfo(vid);
    return { web_uri: webUri };
  } catch (e) {
    return { web_uri: '' };
  }
};
const avatarFormat = avatar => {
  const res = {
    web_uri: '',
    url: '',
    height: 0,
    width: 0,
    ...avatar,
  };
  if (res.web_uri === null || res.web_uri === undefined) {
    return {
      ...res,
      web_uri: '',
    };
  }
  return res;
};
const createCreative = async (): Promise<any> => {
  const isUsingTikTokVideo = adsCreative.getters.identityFormData.value.activeSource === MaterialSource.TIKTOK_VIDEO;
  const usingTikTokVideo = adsCreative.getters.identityFormData.value.activeSource === MaterialSource.TIKTOK_VIDEO;
  const { selectedVideo } = adsCreative.getters.identityFormData.value;

  const createCreativeObject = (mode: number, image: any, video?: CreativeInfo): Record<string, any> => {
    const source = displayName.value;
    const title = adsCreative.state.creative.title.value;
    const avatar = identityStore.getters.identityFormData.value.avatarIcon;
    const itemSource = () =>
      isUsingTikTokVideo
        ? AdSources.NATIVE_VIDEO
        : curIdentity.value.identity_type === IdentityType.TT_USER
        ? AdSources.PUSH_TIKTOK
        : AdSources.CREATE_NEW;
    let creativeItem;
    creativeItem = {
      struct_version: 1,
      source,
      title,
      call_to_action: isEngagement.value ? '' : adsCreative.getters.creativeFormData.value.callToAction,
      avatar_icon: avatarFormat(avatar),
      is_creative_authorized: true,
      media_info: {
        video_info: video?.vid
          ? {
              video_id: video?.vid,
              file_name: '',
            }
          : {},
        image_mode: mode,
        image_info: isEmpty(image) ? [] : [pick(image, ['web_uri', 'name'])],
        ...(isUsingTikTokVideo ? { aweme_item_id: video?.item_id } : {}),
      },
      // 创编类型为traffic或者Reach的时候传external_url
      ...(!isTraffic.value && !isReach.value && !isVideoView.value
        ? {}
        : {
            external_url: adsCreative.getters.creativeFormData.value.externalUrl,
          }),
      // 仅为 engagement objective 添加 advanced_creative_type 字段
      ...(isEngagement.value || isReach.value
        ? {
            advanced_creative_type: isReach.value ? AdvancedCreativeType.CTA : AdvancedCreativeType.EMPTY,
          }
        : {}),
      // video_info: video // 06-16 和后端确认video_info是无用的，会被bff层直接filter out掉，所以传与不传不影响
    };
    if (isLead.value && adsCreative.getters.creativeFormData.value.instantForm.id) {
      creativeItem = {
        ...creativeItem,
        page_id: adsCreative.getters.creativeFormData.value.instantForm.id,
      };
    }
    creativeItem = {
      ...creativeItem,
      identity_type: curIdentity.value?.identity_type,
      identity_id: curIdentity.value?.identity_id,
      item_source: itemSource(),
    };
    return creativeItem;
  };
  const creativeInfo = adsCreative.state.identity.creativeInfo.value;
  const adsCover = usingTikTokVideo
    ? await getCoverInfoAPI(selectedVideo.video_info.vid)
    : await getCoverInfoAPI(creativeInfo.vid);
  const creativeList = [
    createCreativeObject(
      imageMode.VIDEO_TYPE_3,
      adsCover,
      usingTikTokVideo ? { ...selectedVideo.video_info, item_id: selectedVideo.item_id } : creativeInfo,
    ),
  ];
  return creativeList;
};
export const getFastCreationData = async (): Promise<any> => {
  const today = getAccountTodayHMS();
  const adGroupExtraParams = getAdGroupExtraParams();
  const smartInterestBehavior = interestBehaviorState.getters.getDisplayToggle.value
    ? {
        smart_interest_behavior: interestBehaviorState.getters.getSmartInterestSelection.value,
      }
    : {};
  const ad_group = {
    struct_version: 1,
    // name: `${i18n.t('module_adgroup_name')}${today.replace(/[-\s:]/g, '')}`,
    name: adsCreative.getters.creativeFormData.value.adGroupName,
    start_time: adsCreative.getters.budgetFormData.value.startTime,
    end_time: adsCreative.getters.budgetFormData.value.endTime,
    budget: adsCreative.getters.budgetFormData.value.budget,
    schedule_type: adsCreative.getters.budgetFormData.value.scheduleType,
    audience:
      adsCreative.getters.audienceFormData.value.targetType === AutomatedTargetingType.ON
        ? {
            ...automaticAudience,
            automated_targeting: 1,
            particle_locations: adsCreative.getters.audienceFormData.value.location.default,
            ...smartInterestBehavior,
          }
        : {
            ...formatAudienceV2(adsCreative.getters.audienceFormData.value),
            ...smartInterestBehavior,
          },
    ...adGroupExtraParams,
  };
  // U18+需求，当用户选择NO_LIMIT时，动态设置符合规则的年龄二维数组
  if ((isLead.value && adsCreative.getters.audienceFormData.value.targetType === AutomatedTargetingType.ON) || adsCreative.getters.audienceFormData.value.age.value[0] === 'NO_LIMIT') {
    ad_group.audience.age = [
      ['18', '24'],
      ['25', '34'],
      ['35', '44'],
      ['45', '54'],
      ['55', '100'],
    ];
  }
  if (isReach.value || isVideoView.value) {
    ad_group.audience.brand_safety = 6;
  }
  const creativeList = await createCreative();
  const campaignName = `${i18n.t(
    campaignObjectiveI18nKey[adsCreative.getters.campaignFormData.value.objectiveType] || '',
  )}${today.replace(/[-\s:]/g, '')}`;

  if (!creativeList?.length) {
    captureError(new Error('Fastcreation: empty creative list'));
    // 创意为空不能提交
    return;
  }
  return {
    campaign: {
      // 创编接口增加名称参数写死
      objective_type: adsCreative.getters.campaignFormData.value.objectiveType,
      struct_version: 1,
      name: campaignName,
    },
    ad_group,
    creative_list: creativeList,
  };
};
// const adsCreateCheck = async (formData): Promise<any> => request.post(apis.adsCreateCheck, formData);
function adsCreateCheck(data): any {
  return request.post(apis.adsCreateCheck, data);
}
const adsCreate = (data): Promise<any> => request.post(apis.adsCreate, data);
export const goCreateAds = async (): Promise<any> => {
  const formData = await getFastCreationData();
  await adsCreateCheck(formData);
  await adsCreate(formData);
  const activeSource = adsCreative.state.identity.activeSource.value;
  const ifRawVideo = activeSource === MaterialSource.CAMERA_ROLL;

  if (ifRawVideo) {
    const { creativeInfo } = adsCreative.getters.identityFormData.value;
    await uploadCreative(creativeInfo.vid);
  }
};
